<script lang="ts" setup>
/**
 * Le Toaster de l'app, au thème Soleil.
 *
 * Il était NOIR (encre pleine, texte blanc) : un bandeau d'alerte système, qui
 * tranchait sur la page crème et se lisait comme une erreur même pour dire
 * « c'est enregistré ». Il est maintenant une carte blanche, arrondie, à ombre
 * légère, comme les cartes de la page : il vient de l'app, pas du téléphone.
 *
 * Le TYPE se dit par une pastille de couleur à gauche (vert, corail, ambre,
 * ciel), pas par le fond : le message reste lisible en encre sur blanc quel
 * que soit son ton.
 *
 * Un toast qui concerne un enfant porte son AVATAR à la place de la pastille,
 * et son prénom au-dessus du message : c'est lui qui parle. Voir
 * `toastForChild` (src/lib/childToast.ts).
 */
import { Toaster as Sonner, type ToasterProps } from 'vue-sonner'
import { defineComponent, h, markRaw } from 'vue'
import LucideIcon from '@/components/common/LucideIcon.vue'

const props = defineProps<ToasterProps>()

/** Une pastille ronde, fond doux et encre du même ton (cf. IconTile). */
function statusIcon(name: string, tone: string) {
  return markRaw(defineComponent({
    name: `ToastIcon-${name}`,
    render: () => h('span', { class: `toast-status ${tone}`, 'aria-hidden': 'true' }, [
      h(LucideIcon, { name, size: 16 }),
    ]),
  }))
}

const icons = {
  success: statusIcon('check', 'toast-status--success'),
  error: statusIcon('x', 'toast-status--error'),
  warning: statusIcon('triangle-alert', 'toast-status--warning'),
  info: statusIcon('info', 'toast-status--info'),
}
</script>

<template>
  <Sonner
    class="toaster group"
    v-bind="props"
    :icons="icons"
    :style="{
      '--normal-bg': '#ffffff',
      '--normal-text': 'var(--color-ink)',
      '--normal-border': 'var(--color-line)',
      '--border-radius': '20px',
    }"
  />
</template>

<style>
/* La carte : blanche, arrondie, ombre légère et chaude (encre de l'app, pas du
   noir pur), qui la décolle de la page sans l'assombrir. */
[data-sonner-toast][data-styled='true'] {
  padding: 12px 16px 12px 12px;
  gap: 12px;
  box-shadow: 0 10px 28px -10px rgba(43, 38, 32, 0.22), 0 2px 6px rgba(43, 38, 32, 0.06);
  font-family: inherit;
}

[data-sonner-toast][data-styled='true'] [data-title] {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

[data-sonner-toast][data-styled='true'] [data-description] {
  font-size: 13px;
  color: var(--color-ink-3) !important;
}

/* La place de l'icône : 16px chez Sonner, une pastille de 32 chez nous. */
[data-sonner-toast][data-styled='true'] [data-icon] {
  width: 32px;
  height: 32px;
  margin: 0;
}

.toast-status {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
}

.toast-status--success {
  background: var(--color-olive-soft);
  color: var(--color-olive);
}

.toast-status--error {
  background: var(--color-danger-soft);
  color: var(--color-danger);
}

.toast-status--warning {
  background: var(--color-joy-soft);
  color: var(--color-joy);
}

.toast-status--info {
  background: var(--color-sky);
  color: var(--color-sky-ink);
}

/* ── Un enfant qui parle ────────────────────────────────────────────────
   L'avatar prend la place de la pastille, un cran plus grand : c'est un
   visage, il doit se reconnaître. */
[data-sonner-toast][data-styled='true'].toast-child [data-icon] {
  width: 40px;
  height: 40px;
}
</style>
